import {Badge} from '@dropbox/dig-components/badge';
import {Text} from '@dropbox/dig-components/typography';
import {atoms, Box, withShade} from '@dropbox/dig-foundations';
import {UIIcon} from '@dropbox/dig-icons';
import {CheckmarkLine, ChevronDownLine, CircleStandardLine} from '@dropbox/dig-icons/assets';
import type {CoachSubmissionContext, JiraObject, MustAddressItem, Workstream} from 'client';
import {AiWorkstreamAssistService} from 'client/services/AiWorkstreamAssistService';
import {Avatar} from 'components/DSYS/Avatar';
import {RichTextArea} from 'components/DSYS/RichTextArea';
import {useTaskPolling} from 'hooks/useTaskPolling';
import {t} from 'i18next';
import {useCallback, useEffect, useRef, useState} from 'react';

import {useWorkstreamRoadmapInitiatives} from './jiraObjects';
import {convertEditorJsonToMarkdown, getMustAddressSourceLabel} from './WorkstreamAICoachTab';

export type AssistPostGate = {
  guardrails: MustAddressItem[];
  snapshotForChat: AssistGuardrailSnapshotRow[];
};

export type AssistGuardrailSnapshotRow = {
  label: string;
  source_type: string;
  source_id: number | null;
  addressed: boolean;
};

function isAddressed(g: MustAddressItem): boolean {
  return Boolean(g.addressed);
}

/** Text to show in status chat when Assist iterate returns feedback (and optional comment). */
export function extractAssistFeedbackForChat(
  result: Record<string, unknown> | null | undefined
): string {
  if (!result || typeof result !== 'object') return '';
  const parts: string[] = [];
  const c = result.comment;
  if (typeof c === 'string' && c.trim()) {
    parts.push(c.trim());
  }
  const fb = result.feedback;
  if (Array.isArray(fb)) {
    for (const item of fb) {
      if (item && typeof item === 'object' && 'message' in item) {
        const m = String((item as {message: unknown}).message).trim();
        if (m) parts.push(m);
      }
    }
  }
  return parts.join('\n\n');
}

/** Guardrail row for in-thread chat: no panel borders, source chip preserved. */
export const AssistGuardrailChatRow = ({
  item,
  workstream,
  jiraObjects,
}: {
  item: MustAddressItem;
  workstream: Workstream;
  jiraObjects: JiraObject[];
}) => {
  const [sourceOpen, setSourceOpen] = useState(false);
  const [labelExpanded, setLabelExpanded] = useState(false);
  const sourceLabel = getMustAddressSourceLabel(item, workstream, jiraObjects);

  return (
    <Box paddingY="6" width="100%" style={{minWidth: 0}}>
      <Box display="flex" alignItems="flex-start" style={{gap: 8}}>
        <Box style={{marginTop: 4, flexShrink: 0}}>
          <UIIcon
            src={isAddressed(item) ? CheckmarkLine : CircleStandardLine}
            size="small"
            className={atoms({
              color: isAddressed(item) ? 'Success Base' : 'Text Subtle',
            })}
          />
        </Box>
        <Box style={{flex: 1, minWidth: 0}}>
          <Text size="small">{item.label} </Text>
        </Box>
        {sourceLabel && (
          <Badge
            size="xsmall"
            onClick={(e) => {
              e.stopPropagation();
              setSourceOpen((o) => !o);
            }}
            {...withShade({
              className: atoms({
                flexShrink: 0,
                marginTop: '4',
                cursor: 'pointer',
                backgroundColor: sourceOpen ? 'Text Base' : 'Background Subtle',
              }),
            })}
          >
            <Box display="flex" alignItems="center" paddingX="4" style={{gap: 4}}>
              <Text
                size="xsmall"
                className={atoms({
                  color: sourceOpen ? 'Background Base' : 'Text Subtle',
                })}
              >
                {t('workstream_assist_source_label')}
              </Text>
              <UIIcon
                src={ChevronDownLine}
                size="small"
                className={atoms({
                  color: sourceOpen ? 'Background Base' : 'Text Subtle',
                  flexShrink: 0,
                })}
                style={{
                  transform: sourceOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                }}
                aria-hidden
              />
            </Box>
          </Badge>
        )}
      </Box>
      {sourceLabel && sourceOpen && (
        <Box
          marginTop="8"
          borderStyle="Solid"
          borderWidth="1"
          borderColor="Border Subtle"
          padding="8"
          borderRadius="Small"
          display="flex"
          flexDirection="column"
          style={{gap: 4}}
        >
          <Box display="flex" alignItems="flex-start" style={{gap: 8}}>
            {sourceLabel.author ? (
              <Avatar size="small" user={sourceLabel.author} />
            ) : sourceLabel.icon ? (
              <UIIcon
                src={sourceLabel.icon}
                size="small"
                className={atoms({
                  color: 'Text Subtle',
                  flexShrink: 0,
                  marginTop: '4',
                })}
              />
            ) : null}
            <Box
              style={{flex: 1, minWidth: 0}}
              cursor="pointer"
              onClick={(e) => {
                e.stopPropagation();
                setLabelExpanded((v) => !v);
              }}
            >
              <RichTextArea
                value={sourceLabel.label}
                editable={false}
                truncateLines={labelExpanded ? undefined : 2}
                textColor="subtle"
                textSize="small"
                clickLinks
              />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export type WorkstreamAssistPinnedStripProps = {
  guardrails: MustAddressItem[];
  satisfiedCount: number;
  total: number;
  expanded: boolean;
  onToggleExpanded: () => void;
};

export const WorkstreamAssistPinnedStrip = ({
  guardrails,
  satisfiedCount,
  total,
  expanded,
  onToggleExpanded,
}: WorkstreamAssistPinnedStripProps) => {
  if (total === 0) return null;
  return (
    <Box
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      paddingX="8"
      paddingY="6"
      backgroundColor="Background Subtle"
      borderBottom="Solid"
      borderColor="Border Subtle"
      borderWidth="1"
      style={{cursor: 'pointer', flexShrink: 0}}
      onClick={() => onToggleExpanded()}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleExpanded();
        }
      }}
      role="button"
      tabIndex={0}
      aria-expanded={expanded}
      aria-label={t('workstream_assist_strip_summary', {done: satisfiedCount, total})}
    >
      <Box
        display="flex"
        alignItems="center"
        style={{gap: 8, flexWrap: 'wrap', flex: 1, minWidth: 0}}
      >
        {!expanded &&
          guardrails.map((g, i) => (
            <Box key={i} title={g.label} paddingTop="4" style={{flexShrink: 0}}>
              <UIIcon
                src={isAddressed(g) ? CheckmarkLine : CircleStandardLine}
                size="small"
                className={atoms({
                  color: isAddressed(g) ? 'Success Base' : 'Text Subtle',
                })}
              />
            </Box>
          ))}
        <Text size="small" color="subtle">
          {t('workstream_assist_strip_summary', {done: satisfiedCount, total})}
        </Text>
      </Box>
      <UIIcon
        src={ChevronDownLine}
        size="small"
        className={atoms({flexShrink: 0})}
        style={{
          transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s',
        }}
        aria-hidden
      />
    </Box>
  );
};

export function useWorkstreamAssist({
  workstream,
  draftText,
  selectedStatus,
  onAssistPostGateChange,
  onCoachContextChange,
  onAssistIterateFeedback,
  enabled = true,
}: {
  workstream: Workstream;
  draftText?: string;
  selectedStatus?: string;
  onAssistPostGateChange?: (gate: AssistPostGate | null) => void;
  onCoachContextChange?: (ctx: CoachSubmissionContext | null) => void;
  onAssistIterateFeedback?: (text: string) => void;
  /** When false, coach/assist suggestions are disabled (e.g. autodraft mode). */
  enabled?: boolean;
}) {
  const [guardrails, setGuardrails] = useState<MustAddressItem[]>([]);
  const onAssistIterateFeedbackRef = useRef(onAssistIterateFeedback);
  onAssistIterateFeedbackRef.current = onAssistIterateFeedback;
  const onAssistPostGateChangeRef = useRef(onAssistPostGateChange);
  onAssistPostGateChangeRef.current = onAssistPostGateChange;
  const onCoachContextChangeRef = useRef(onCoachContextChange);
  onCoachContextChangeRef.current = onCoachContextChange;

  const {data: jiraObjects = []} = useWorkstreamRoadmapInitiatives(workstream.id, true);

  const draftMarkdown = draftText ? convertEditorJsonToMarkdown(draftText) : '';

  const initialComplete = useCallback((result: Record<string, unknown>) => {
    const raw = result?.guardrails as MustAddressItem[] | undefined;
    const list = Array.isArray(raw) ? raw : [];
    setGuardrails(list);
  }, []);

  const {start: startInitial, isPolling: isInitialPolling} = useTaskPolling<
    Record<string, unknown>,
    []
  >({
    queryKeyPrefix: ['workstream', 'assist', 'initial', workstream.id],
    startTask: () =>
      AiWorkstreamAssistService.startAssistInitialApiV1WorkstreamAssistInitialPost({
        workstream_id: workstream.id,
        selected_status: selectedStatus,
      }),
    getStatus: async (taskId) => {
      const r =
        await AiWorkstreamAssistService.getAssistInitialStatusApiV1WorkstreamAssistInitialGenerateTaskIdGet(
          taskId
        );
      return {status: r.status, result: r.result as Record<string, unknown> | null};
    },
    enabled: enabled && workstream.id > 0,
    onCompleted: (result) => {
      initialComplete(result);
    },
    onFailed: () => {
      setGuardrails([]);
    },
  });

  const startInitialRef = useRef(startInitial);
  startInitialRef.current = startInitial;
  const initialKeyRef = useRef<number | null>(null);
  useEffect(() => {
    if (workstream.id <= 0) return;
    if (initialKeyRef.current === workstream.id) return;
    initialKeyRef.current = workstream.id;
    startInitialRef.current();
  }, [workstream.id]);

  const {start: startIterate} = useTaskPolling<Record<string, unknown>, [string]>({
    queryKeyPrefix: ['workstream', 'assist', 'iterate', workstream.id],
    startTask: (markdownDraft: string) =>
      AiWorkstreamAssistService.startAssistIterateApiV1WorkstreamAssistIteratePost({
        workstream_id: workstream.id,
        draft: markdownDraft,
        must_address: guardrails,
        selected_status: selectedStatus,
      }),
    getStatus: async (taskId) => {
      const r =
        await AiWorkstreamAssistService.getAssistIterateStatusApiV1WorkstreamAssistIterateGenerateTaskIdGet(
          taskId
        );
      return {status: r.status, result: r.result as Record<string, unknown> | null};
    },
    enabled: enabled && workstream.id > 0 && guardrails.length > 0,
    onCompleted: (result) => {
      const raw = result?.guardrails as MustAddressItem[] | undefined;
      if (Array.isArray(raw)) {
        setGuardrails(raw);
      }
      const chatText = extractAssistFeedbackForChat(result as Record<string, unknown>);
      if (chatText) {
        onAssistIterateFeedbackRef.current?.(chatText);
      }
    },
    onFailed: () => {},
  });

  const startIterateRef = useRef(startIterate);
  startIterateRef.current = startIterate;
  useEffect(() => {
    if (!draftMarkdown.trim() || guardrails.length === 0) return;
    const timeoutId = setTimeout(() => {
      startIterateRef.current(draftMarkdown);
    }, 1500);
    return () => clearTimeout(timeoutId);
  }, [draftMarkdown, guardrails.length]);

  useEffect(() => {
    const notifyGate = onAssistPostGateChangeRef.current;
    if (!notifyGate) return;
    const snapshotForChat: AssistGuardrailSnapshotRow[] = guardrails.map((g) => ({
      label: g.label ?? '',
      source_type: g.source_type ?? 'general',
      source_id: g.source_id ?? null,
      addressed: isAddressed(g),
    }));
    notifyGate({
      guardrails,
      snapshotForChat,
    });
  }, [guardrails]);

  useEffect(() => {
    const notifyCoach = onCoachContextChangeRef.current;
    if (!notifyCoach) return;
    if (guardrails.length > 0) {
      notifyCoach({
        must_address: guardrails as unknown as null,
        coach_mode: 'assist',
      } as CoachSubmissionContext);
    } else {
      notifyCoach(null);
    }
  }, [guardrails]);

  const satisfiedCount = guardrails.filter((g) => isAddressed(g)).length;
  const total = guardrails.length;

  const showAssistThread =
    enabled && workstream.id > 0 && (isInitialPolling || guardrails.length > 0);

  return {
    guardrails,
    jiraObjects,
    isInitialPolling,
    satisfiedCount,
    total,
    showAssistThread,
  };
}
